import type { TFunction } from "i18next";
import type { ClankPermissions, Company, SpecifyUserPermission } from "../api/types";
import type { User, UserWhoami } from "../api";

const NO_COMPANY = "No company";

/**
 * The people a viewer may pick in a user filter, current user marked. Once they span more than
 * one company they are grouped by it — a single header wrapping everyone would be noise. A viewer
 * may see users from a company they cannot name (user and company visibility are separate
 * permissions), so a company missing from `companies` falls back to its id.
 */
export const buildVisibleUserOptions = (
  permission: SpecifyUserPermission,
  users: User[],
  user: UserWhoami,
  t: TFunction,
  companies: Company[] = [],
) => {
  const visible = users.filter(candidate => canSeeUser(permission, user, candidate.id));
  const grouped = new Set(visible.map(candidate => candidate.company_id)).size > 1;
  const names = new Map(companies.map(company => [company.id, company.name]));
  const options = visible.map(candidate => {
    const displayName =
      `${candidate.first_name ?? ""} ${candidate.last_name ?? ""}`.trim() || candidate.username;
    const companyId = candidate.company_id;
    return {
      id: candidate.id,
      label: candidate.id === user.id ? t("people_you", { name: displayName }) : displayName,
      shortcutLabel: candidate.id === user.id ? t("people_only_you") : undefined,
      group: !grouped
        ? undefined
        : companyId == null
          ? NO_COMPANY
          : (names.get(companyId) ?? `Company ${companyId}`),
    };
  });
  // Named companies first, alphabetically; the unnamed bucket is a leftover, so it sits last.
  return options.sort((a, b) => {
    if (a.group === b.group) return 0;
    if (a.group === NO_COMPANY) return 1;
    if (b.group === NO_COMPANY) return -1;
    return (a.group ?? "").localeCompare(b.group ?? "");
  });
};

export const canSeeUser = (
  permission: SpecifyUserPermission,
  user: Pick<UserWhoami, "id">,
  targetUserId: string,
): boolean => {
  if (permission === "OnlyMine") return targetUserId === user.id;
  if (permission === "AllCompany" || permission === "All") return true;
  return targetUserId === user.id || permission.SpecificUsers.includes(targetUserId);
};

export const canSeeScreenshotsForUser = (
  permissions: ClankPermissions,
  user: Pick<UserWhoami, "id">,
  targetUserId: string,
): boolean => canSeeUser(permissions.screenshots.visualization, user, targetUserId);

export const canSeeExecutiveDashboard = (permissions: ClankPermissions): boolean =>
  permissions.is_admin || permissions.users.creation;

export const canManageUsers = (permissions: ClankPermissions): boolean => permissions.is_admin;
export const isAdmin = (permissions: ClankPermissions): boolean => permissions.is_admin;
